export type HomepageTileOccurrence<TPhoto> = {
    key: string;
    photo: TPhoto;
    rowKey: string;
    left: number;
    top: number;
    width: number;
    height: number;
};

export type HomepageTileRecord<TPhoto> = {
    photo: TPhoto;
    rowKey: string;
};

export type HomepageTileReconciliation<TRecord> = {
    records: TRecord[];
    recordsByKey: Map<string, TRecord>;
    obsoleteRecords: TRecord[];
};

export type HomepageTileActivation = {
    shouldOpen: boolean;
    suppressNextTileClick: boolean;
};

export function createHomepageTileOccurrenceKey(sequenceIndex: number, imageKey: string, occurrenceOrdinal: number): string {
    return `${sequenceIndex}:${imageKey.length}:${imageKey}:${occurrenceOrdinal}`;
}

export function reconcileHomepageTileOccurrences<TPhoto, TRecord extends HomepageTileRecord<TPhoto>>(
    occurrences: readonly HomepageTileOccurrence<TPhoto>[],
    existingRecordsByKey: ReadonlyMap<string, TRecord>,
    createRecord: (occurrence: HomepageTileOccurrence<TPhoto>) => TRecord,
    updateRecord: (record: TRecord, occurrence: HomepageTileOccurrence<TPhoto>) => void
): HomepageTileReconciliation<TRecord> {
    const records: TRecord[] = [];
    const recordsByKey = new Map<string, TRecord>();

    for (const occurrence of occurrences) {
        const record = existingRecordsByKey.get(occurrence.key) ?? createRecord(occurrence);
        record.photo = occurrence.photo;
        record.rowKey = occurrence.rowKey;
        updateRecord(record, occurrence);
        records.push(record);
        recordsByKey.set(occurrence.key, record);
    }

    const obsoleteRecords: TRecord[] = [];
    for (const [key, record] of existingRecordsByKey) {
        if (!recordsByKey.has(key)) obsoleteRecords.push(record);
    }

    return { records, recordsByKey, obsoleteRecords };
}

export function resolveHomepageTileActivation(
    suppressNextTileClick: boolean,
    pinchAwaitingRelease: boolean
): HomepageTileActivation {
    if (!suppressNextTileClick) return { shouldOpen: true, suppressNextTileClick: false };
    return { shouldOpen: false, suppressNextTileClick: pinchAwaitingRelease };
}
